import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import queryString from 'query-string'
import { trackInternalEvent, trackMarketingEvent } from '@rio/tracking'
import { useLocation, useParams } from 'react-router-dom'
import PreviewPanel from 'builder/components/PreviewPanel'
import {
  DocumentCustomizationModalSwitcher,
  prettifySize,
  useTemplateCustomization,
  useUpdateTemplateSettings,
} from 'builder/components/DocumentCustomizationModal'
import FloatingPreviewButton from 'builder/components/FloatingPreviewButton'
import {
  actions,
  ColorChangeHandler,
  FontChangeHandler,
  LayoutFormatChangeHandler,
  PrefillStatuses,
  SizeChangeHandler,
  SpacingChangeHandler,
  TemplateChangeHandler,
  selectors,
} from 'builder/modules/resumeEditor'
import {
  ResumeCritiqueStatuses,
  actions as critiqueActions,
} from 'builder/modules/resumeCritiqueReview'
import {
  selectors as resumeReviewSelectors,
  actions as resumeReviewActions,
} from 'builder/modules/resumeReview'
import { actions as coverLetterActions } from 'builder/modules/coverLetterEditor'
import { actions as panelActions } from 'builder/modules/panel'
import { actions as coRegActions } from 'builder/modules/coRegistration'
import { actions as generalEditorActions } from 'builder/modules/generalEditor'
import { navigate } from 'builder/modules/navigate'
import TunerPrePaywallModal from 'builder/components/TunerPrePaywallModal'
import { JobPosting } from 'builder/components/DocumentEditor/JobPosting/JobPosting'
import AiCoverletterTailoredView from 'builder/components/DocumentEditor/AICoverLetter/AiCoverletterTailoredView'
import SavingInfoBanner from 'builder/components/JobTracking/SavingInfoBanner/SavingInfoBanner'
import { AICoverLetter } from 'builder/components/DocumentEditor/JobPosting/AiCoverLetter'
import EditAIJobDetails from 'builder/components/DocumentEditor/AICoverLetter/EditAIJobDetails'
import { DocumentTypes, FetchStatuses, SPACING } from 'builder/modules/constants'
import { ErrorState } from 'builder/components/DocumentEditor/AICoverLetter/ErrorState'
import { ButtonSize } from 'builder/components/Button'
import AbandonmentModal from 'builder/components/AbandonmentModal'
import AbandonmentResumeModal from 'builder/components/AbandonmentResumeModal'
import { HAS_ABANDONMENT_RESUME_MODAL_BEEN_SHOW } from 'builder/components/AbandonmentResumeModal/constants'
import { UserType } from 'builder/modules/user'
import Paywall from 'builder/components/AiFeatures/Paywall'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useConfig } from 'builder/hooks/useConfig'
import { useEffectOnMount } from 'builder/hooks/useEffectOnMount'
import { MouseClickEvent } from 'builder/modules/ui'
import { ResumeDistributionModal } from 'builder/components/ResumeDistributionModal'
import { useDocumentDownload } from 'builder/components/DocumentEditor'
import { useStepByStepFeature } from 'builder/components/DocumentEditor/StepByStepFlow/hooks/useStepByStepFeature'
import { OverviewModal } from 'builder/components/AutoTailoring/OverviewModal'
import { CritiqueFinishedBanner } from 'builder/components/CritiqueFinishedBanner/CritiqueFinishedBanner'
import { IFRAME_PARAMS } from 'builder/modules/resumeCritiqueReview/constants'
import { ResumeEditorSwitcher } from './ResumeEditorSwitcher'
import { Container, LeftSide } from './styles'

export const ResumeEditorView = () => {
  const location = useLocation()
  const routeParams = useParams()
  const mediaQueries = useMediaQueries()
  const config = useConfig()
  const dispatch = useDispatch()
  const user = useTypedSelector(state => state.user)
  const isDownloading = useTypedSelector(state => state.rendering.isDocumentDownloading)
  const editorState = useTypedSelector(state => state.generalEditor)
  const resumeState = useTypedSelector(state => state.resumeEditor)
  const coverLetterState = useTypedSelector(state => state.coverLetterEditor)
  const panelState = useTypedSelector(state => state.panel)
  const isPlansPageAbandonmentModalOpen = useTypedSelector(
    state => state.ui.isPlansPageAbandonmentModalOpen,
  )
  const [hasAbandonmentModalBeenShown, setHasAbandonmentModalBeenShown] = useState(false)
  const [isUserLeaving, setIsUserLeaving] = useState(false)
  const isCustomizeModalOpen = useTypedSelector(state => state.resumeEditor.isCustomizeModalOpen)

  const resumeId = (routeParams.id && parseInt(routeParams.id)) || 0

  const [showResumeDistributionModal, setShowResumeDistributionModal] = useState(false)
  const [triggerDownload, setTriggerDownload] = useState(false)
  const isOnline = useTypedSelector(state => state.application.isOnline)
  const { status: prefillingStatus } = useTypedSelector(selectors.prefill)
  const isPrefilling = prefillingStatus === PrefillStatuses.scheduled

  const critique = useTypedSelector(state => resumeReviewSelectors.critique(state, resumeId))
  const critiqueReviewStatus = critique?.status
  const isCritiqueReviewStatusFinished = critiqueReviewStatus === ResumeCritiqueStatuses.finished
  const critiqueEntryPoints = config?.features.critiqueEntryPoints

  const {
    resume,
    isSyncing,
    isOptimizerModalOpen,
    isOpenSavingInfoBanner,
    changeJobPostingLink,
    isClickedOnAiCVBanner,
    optimizerSection: {
      jobPostingAPIData: { recommendedJobTitle },
    },
    AICoverLetter: {
      generateAICoverLetterStatus,
      isOpenAICVOptimizerModal,
      isClickedOnAiCVEditModalIcon,
      isShowUntailoreView,
    },
    aiGenerationPaywallType,
  } = resumeState

  const { updateTemplateSettings } = useUpdateTemplateSettings(resume)
  const { supportsCustomColor, selectedLayoutFormatOption, supportsNewCustomization } =
    useTemplateCustomization(resume)
  const { handleDownloadDocument } = useDocumentDownload(resume)

  const { isRbpStepFlowActive } = useStepByStepFeature()
  const abandonmentModalListener = useCallback((e: MouseEvent) => {
    if (e.pageY <= 5) {
      setIsUserLeaving(false)
    }
  }, [])

  const init = useCallback(() => {
    // fetch data and initialize related modules
    if (!resumeId) {
      return
    }

    dispatch(actions.fetchResumeRequest({ id: resumeId }))
    dispatch(generalEditorActions.fetchEditorPayloadRequest())
    dispatch(coRegActions.initialize())

    // show the preview modal on mobiles
    const params = queryString.parse(location.search, { parseNumbers: true })
    if ('preview' in params && !mediaQueries.isDesktop) {
      dispatch(actions.setIsCustomizeModalOpen(true))
    }
    // track the page opening

    const isDataLayerPushed = localStorage.getItem('DATA_LAYER_PUSH')
    const hasAbandonmentModalBeenShown = localStorage.getItem(
      HAS_ABANDONMENT_RESUME_MODAL_BEEN_SHOW,
    )

    if (hasAbandonmentModalBeenShown === 'true') {
      setHasAbandonmentModalBeenShown(true)
    }

    if (user?.data?.socialAccount && !(isDataLayerPushed === 'true')) {
      localStorage.setItem('DATA_LAYER_PUSH', 'true')
      trackMarketingEvent('Sign up', 'Complete Registration')
    }

    if (panelState.documentsStatus !== 'LOADED') {
      // If someone has opened a resume directly, we call this action to get info of user documents
      dispatch(panelActions.fetchDocuments())
    }

    document.addEventListener('mousemove', abandonmentModalListener)
  }, [
    abandonmentModalListener,
    dispatch,
    location.search,
    mediaQueries.isDesktop,
    panelState.documentsStatus,
    resumeId,
    user?.data?.socialAccount,
  ])

  useEffect(() => {
    init()
  }, [routeParams.id])

  useEffectOnMount(() => {
    return () => {
      document.removeEventListener('mousemove', abandonmentModalListener)
    }
  })

  useEffect(() => {
    dispatch(resumeReviewActions.fetchCritiques())
  }, [dispatch])

  useEffect(() => {
    const {
      isClickedOnAiCVBanner,
      optimizerSection: {
        jobPostingAPIData: { recommendedJobTitle },
      },
      AICoverLetter: { isShowUntailoreView },
    } = resumeState

    const isJobPostingLinked = recommendedJobTitle !== null && recommendedJobTitle !== ''

    const AICoverLetterTailoredView =
      isClickedOnAiCVBanner && isJobPostingLinked && !isShowUntailoreView

    if (AICoverLetterTailoredView) {
      trackInternalEvent('see_job_posting_selection_screen')
    }

    if (resumeState.error) navigate('/resumes')
  }, [resumeState])

  const sendResumeToCritiqueReview = (resumeId: number) => {
    if (user?.data?.billingInfo.userType === UserType.free) {
      return
    }

    trackInternalEvent('click_send_resume_review', { where: 'dash' })

    dispatch(
      resumeReviewActions.sendResumeToCritique({
        resumeId,
      }),
    )
  }

  useEffect(() => {
    const resumeIdToSendToCritique = sessionStorage.getItem('sendToCritique')
    if (resumeIdToSendToCritique) {
      sendResumeToCritiqueReview(Number(resumeIdToSendToCritique))
      sessionStorage.removeItem('sendToCritique')
    }
  }, [])

  const handleFullSizeOpen = useCallback(
    (e?: MouseClickEvent) => {
      e && e.preventDefault()
      dispatch(actions.setIsCustomizeModalOpen(true))
    },
    [dispatch],
  )

  const handleFullSizeClose = useCallback(() => {
    dispatch(actions.setIsCustomizeModalOpen(false))
  }, [dispatch])

  const handleBannerClose = useCallback(() => {
    dispatch(actions.setOpenSavingInfoBanner(false))
  }, [dispatch])

  const handleBannerOpen = useCallback(() => {
    navigate('/job-tracking')
    trackInternalEvent('open_job_tracking')
  }, [])

  const handleAbandonmentModalClose = useCallback(() => {
    setHasAbandonmentModalBeenShown(true)
    setIsUserLeaving(false)
  }, [])

  const handleTemplateUpdate: TemplateChangeHandler = useCallback(
    id => {
      dispatch(actions.updateTemplate({ id }))
      trackInternalEvent('change_resume_template', { template: id })
    },
    [dispatch],
  )

  const resetGenerateAICoverLetterStatus = useCallback(() => {
    dispatch(actions.setGenerateAICoverLetterStatus(FetchStatuses.notAsked))
  }, [dispatch])

  const onRetryAIGenerateErrorStateModal = useCallback(() => {
    resetGenerateAICoverLetterStatus()
    dispatch(actions.setIsClickedOnAiCVBanner(true))
  }, [dispatch, resetGenerateAICoverLetterStatus])

  const handleFloatingButtonClick = useCallback(() => {
    dispatch(actions.setIsCustomizeModalOpen(true))
    trackInternalEvent('open_full_screen_resume_preview')
    trackInternalEvent('click_download_resume_button', {
      source: 'resume_editor',
      template: resume?.template,
      format: 'pdf',
      score: resume?.resumeScore,
    })
  }, [dispatch, resume?.resumeScore, resume?.template])

  const {
    aiGenerationSection: { showPaywall: showAICoverLetterPaywall },
  } = coverLetterState

  const isDataLoaded = !!resume && editorState.isLoaded

  const showResumeOptimizerModal = isOptimizerModalOpen && resume && user.data
  const isJobPostingLinked = recommendedJobTitle !== null && recommendedJobTitle !== ''
  const showAICoverLetterTailoredView =
    isClickedOnAiCVBanner && isJobPostingLinked && !isShowUntailoreView
  const howAiCoverLetterNormalView =
    (isClickedOnAiCVBanner && !isJobPostingLinked) ||
    isOpenAICVOptimizerModal ||
    isShowUntailoreView

  const showGenerateCLErrorStateModal = generateAICoverLetterStatus === FetchStatuses.failed

  const errorStateButtonSize = !mediaQueries.isPhone ? ButtonSize.default : ButtonSize.small
  const inBuilderAbandonmentFlag = config?.features.inBuilderAbandonment || false
  const showAbandonmentModal =
    !hasAbandonmentModalBeenShown &&
    [UserType.free, UserType.postPremium].includes(user?.data?.billingInfo.userType as UserType) &&
    isUserLeaving &&
    inBuilderAbandonmentFlag &&
    user?.data?.showAbandonmentFlows

  const handleClosePaywall = useCallback(() => {
    if (showAICoverLetterPaywall) {
      dispatch(coverLetterActions.setShowAICoverLetterPaywall())
    } else {
      dispatch(actions.setAiGenerationPaywallType(null))
    }
  }, [dispatch, showAICoverLetterPaywall])

  const handleColorSelect: ColorChangeHandler = useCallback(
    color => {
      if (color && supportsCustomColor && supportsNewCustomization) {
        updateTemplateSettings({
          settingsName: 'color',
          value: color,
        })
      } else {
        dispatch(
          actions.updateSimpleField({
            name: 'color',
            value: color,
            debounce: false,
          }),
        )
      }
      trackInternalEvent('change_resume_color', { template: resume?.template })
    },
    [
      supportsCustomColor,
      supportsNewCustomization,
      updateTemplateSettings,
      dispatch,
      resume?.template,
    ],
  )

  const handleChangeLayoutFormat: LayoutFormatChangeHandler = useCallback(
    value => {
      if (typeof value !== 'string') return
      updateTemplateSettings({
        settingsName: 'format',
        value,
      })
      trackInternalEvent('change_page_size_format', {
        old_value: selectedLayoutFormatOption,
        new_value: value,
        template: resume?.template,
      })
    },
    [resume?.template, updateTemplateSettings, selectedLayoutFormatOption],
  )

  const handleFontChange: FontChangeHandler = useCallback(
    ({ value, newFontType, selectedPrimaryFont, selectedSecondaryFont, selectedTertiaryFont }) => {
      if (!resume?.template || typeof value !== 'string' || !newFontType) return

      const oldValue =
        newFontType === 'primaryFont'
          ? selectedPrimaryFont
          : newFontType === 'secondaryFont'
          ? selectedSecondaryFont
          : selectedTertiaryFont

      const eventFontType =
        newFontType === 'primaryFont'
          ? 'primary'
          : newFontType === 'secondaryFont'
          ? 'secondary'
          : 'tertiary'

      updateTemplateSettings({
        settingsName: newFontType,
        value,
      })

      trackInternalEvent('change_resume_font', {
        label: eventFontType,
        old_value: oldValue,
        new_value: value,
        template: resume?.template,
      })
    },
    [resume?.template, updateTemplateSettings],
  )

  const handleSizeChange: SizeChangeHandler = useCallback(
    (sizeNew: number, sizeOld: number) => {
      if (!resume?.template || typeof sizeNew !== 'number') return
      updateTemplateSettings({
        settingsName: 'fontSize',
        value: sizeNew,
      })
      trackInternalEvent('change_resume_font_size', {
        old_value: prettifySize(sizeOld),
        new_value: prettifySize(sizeNew),
        template: resume.template,
      })
    },
    [resume?.template, updateTemplateSettings],
  )

  const handleSpacingChange: SpacingChangeHandler = useCallback(
    (spacingNew: number, spacingOld: number) => {
      const normalize = (value: number) => Math.min(Math.max(value, SPACING.min), SPACING.max)

      if (supportsNewCustomization) {
        if (typeof spacingNew !== 'number') return
        updateTemplateSettings({
          settingsName: 'lineSpacing',
          value: spacingNew,
        })
      } else {
        dispatch(
          actions.updateSimpleField({
            name: 'spacing',
            value: normalize(spacingNew),
            debounce: true,
          }),
        )
      }

      trackInternalEvent('change_line_spacing', {
        label: 'templates',
        direction: spacingNew > spacingOld ? 'increase' : 'decrease',
        old_value: spacingOld,
        new_value: spacingNew,
      })
    },
    [dispatch, supportsNewCustomization, updateTemplateSettings],
  )

  const viewResumeCritiqueReview = (url?: string) => {
    if (url) {
      trackInternalEvent('click_read_resume_critique_banner', { where: 'builder' })
      dispatch(critiqueActions.setCritiquePageVisitedCount(true))
      window.open(`${url}${IFRAME_PARAMS}`)
    }
  }

  return (
    <>
      <TunerPrePaywallModal />
      {resume?.autoTailored && resume.draft ? (
        <OverviewModal />
      ) : (
        <Container>
          {isCritiqueReviewStatusFinished && critiqueEntryPoints && (
            <CritiqueFinishedBanner
              reviewerName={critique?.reviewer.name || 'Joe Miller'}
              viewResumeCritiqueReview={() => viewResumeCritiqueReview(critique?.iframeUrl || '')}
            />
          )}

          <LeftSide>
            <ResumeEditorSwitcher
              editorState={editorState}
              resumeState={resumeState}
              isDataLoaded={isDataLoaded}
              user={user}
              handleOpenCustomize={handleFullSizeOpen}
            />
          </LeftSide>

          {/**
           * Unmount PreviewPanel when DocumentPreviewModal is visible. Reasons:
           * - prevent render if block is invisible - reduce the load of user's device
           * - prevent parallel tasks - pdfjs worker can't do more than one in parallel
           */}
          {!isCustomizeModalOpen && mediaQueries.isDesktop && resume && (
            <PreviewPanel
              type={DocumentTypes.resume}
              onFullSize={handleFullSizeOpen}
              documentId={resumeId}
              isSyncing={isSyncing}
              isDownloading={isDownloading}
              currentDocument={resume}
              isFullScreen={isCustomizeModalOpen}
              showResumeDistributionModal={() => setShowResumeDistributionModal(true)}
              triggerDownload={triggerDownload}
              isOnline={isOnline}
              isPrefilling={isPrefilling}
            />
          )}

          {isCustomizeModalOpen && resume && (
            <DocumentCustomizationModalSwitcher
              type={DocumentTypes.resume}
              isSyncing={isSyncing}
              isDownloading={isDownloading}
              currentDocument={resume}
              editorState={editorState}
              onClose={handleFullSizeClose}
              handleDownloadDocument={handleDownloadDocument}
              updateTemplate={handleTemplateUpdate}
              triggerDownload={triggerDownload}
              showResumeDistributionModal={() => setShowResumeDistributionModal(true)}
              registeredAt={user?.data?.registeredAt}
              showAbandonmentFlows={user?.data?.showAbandonmentFlows}
              handleColorSelect={handleColorSelect}
              handleChangeLayoutFormat={handleChangeLayoutFormat}
              handleFontChange={handleFontChange}
              handleSizeChange={handleSizeChange}
              handleSpacingChange={handleSpacingChange}
            />
          )}

          {mediaQueries.isLaptop && isDataLoaded && !isRbpStepFlowActive && (
            <FloatingPreviewButton isSaving={isSyncing} onClick={handleFloatingButtonClick} />
          )}
        </Container>
      )}
      {showAICoverLetterTailoredView && <AiCoverletterTailoredView />}
      {showResumeOptimizerModal && <JobPosting resumeId={resumeId} />}
      {howAiCoverLetterNormalView && <AICoverLetter resumeId={resumeId} />}
      {isClickedOnAiCVEditModalIcon && <EditAIJobDetails />}
      {isOpenSavingInfoBanner && !mediaQueries.isPhone && !changeJobPostingLink && (
        <SavingInfoBanner onClose={handleBannerClose} onOpen={handleBannerOpen} />
      )}
      {aiGenerationPaywallType && (
        <Paywall onClose={handleClosePaywall} aiGenerationType={aiGenerationPaywallType} />
      )}
      {isPlansPageAbandonmentModalOpen && <AbandonmentModal />}

      {showGenerateCLErrorStateModal && (
        <ErrorState
          onClose={resetGenerateAICoverLetterStatus}
          onTryAgain={onRetryAIGenerateErrorStateModal}
          ButtonSize={errorStateButtonSize}
        />
      )}
      {showAbandonmentModal && user.data && (
        <AbandonmentResumeModal
          handleClose={handleAbandonmentModalClose}
          hasUserVisitedPlans={user.data.visitedPlans}
          email={user.data.email}
          fullName={user.data.fullName}
        />
      )}

      {showResumeDistributionModal && (
        <ResumeDistributionModal
          onClose={() => setShowResumeDistributionModal(false)}
          triggerDownload={() => setTriggerDownload(true)}
        />
      )}
    </>
  )
}
